import { SummaryGridItem, SummarySection, getPromotionTitle } from '@css-xp/base';
import {
  CommerceAddress,
  InvoiceGroup,
  TransactionAccount,
  BillingAdmin,
  Maybe,
  Promotion,
} from '@css-xp/graphtypes/src/generated-types-new';
import { defaultTo } from 'lodash';
import React, { useMemo } from 'react';
import { BillingAccount } from './progress-forms/billing-account-table';
import { formatPaymentMethod, getInvoiceGroupName } from './progress-forms/billing-profile-table';

interface Props {
  previousTransactionAccount: TransactionAccount;
  previousInvoiceGroup: InvoiceGroup;
  previousBillingAdmin?: string | null;
  newTransactionAccount?: TransactionAccount;
  newInvoiceGroup?: InvoiceGroup;
  newBillingAdmin?: string | null;
  newPartnerDiscount?: Promotion;
  isFinalStep?: boolean;
}

export const ReviewAndSubmitChange = ({
  previousTransactionAccount,
  previousInvoiceGroup,
  previousBillingAdmin,
  newTransactionAccount,
  newInvoiceGroup,
  newPartnerDiscount,
  newBillingAdmin,
  isFinalStep = false,
}: Props) => {
  const [previousShowMore, setPreviousShowMore] = React.useState(false);
  const [newShowMore, setNewShowMore] = React.useState(false);

  const formatAddressLabel = (node: CommerceAddress) => {
    let formatAddressLabel1 = '';
    let formatAddressLabel2 = '';
    if (node?.addressLine1) {
      formatAddressLabel1 += node.addressLine1;
    }
    if (node?.addressLine2) {
      formatAddressLabel1 += `, ${node.addressLine2}`;
    }
    if (node?.city) {
      formatAddressLabel2 += `${node.city}`;
    }
    if (node?.state) {
      formatAddressLabel2 += `, ${node.state}`;
    }
    if (node?.postcode) {
      formatAddressLabel2 += ` ${node.postcode}`;
    }
    if (node?.country) {
      formatAddressLabel2 += `, ${node.country}`;
    }
    return (
      <div>
        {formatAddressLabel1}
        <br></br>
        {formatAddressLabel2}
      </div>
    );
  };

  const selectedCurrentBillingAdmin = useMemo(() => {
    return previousBillingAdmin
      ? previousBillingAdmin
      : previousTransactionAccount?.billingAdmins?.nodes?.[0]?.email
      ? previousTransactionAccount.billingAdmins.nodes[0].email
      : '';
  }, [previousBillingAdmin, previousTransactionAccount]);

  const selectedNewBillingAdmin = useMemo(() => {
    return newBillingAdmin
      ? newBillingAdmin
      : newTransactionAccount?.billingAdmins?.nodes?.[0]?.email
      ? newTransactionAccount.billingAdmins.nodes[0].email
      : '';
  }, [newBillingAdmin, newTransactionAccount]);

  const previousTxaBillingAdminsCount = useMemo(() => {
    return previousTransactionAccount?.billingAdmins?.nodes?.length ?? 0;
  }, [previousTransactionAccount]);
  const newTxaBillingAdminsCount = useMemo(() => {
    return newTransactionAccount?.billingAdmins?.nodes?.length ?? 0;
  }, [newTransactionAccount]);
  const loadMore = (billingAdmins: Maybe<BillingAdmin>[] | undefined | null, selectedBillingAdmin: string) => {
    return billingAdmins
      ?.filter((billingAdmin: Maybe<BillingAdmin>) => billingAdmin?.email !== selectedBillingAdmin)
      .map((billingAdmin: Maybe<BillingAdmin>, index: number) => <div key={index}>{billingAdmin?.email}</div>);
  };

  return (
    <div className={`plan-view ${!isFinalStep && 'order-detail-txa-review'}`}>
      <h2>{isFinalStep ? 'Review and submit' : ''}</h2>
      <div className="review-details-container" data-testid="billing-profiles-review-details">
        <SummarySection className="summary-section-internal review-details">
          <SummaryGridItem className="section-item-internal">
            <SummarySection columns="1fr 1fr" className="summary-section-internal">
              <SummaryGridItem className="section-item-internal review-item review-item-old">
                <h3 className="main-head">From {!isFinalStep && 'account details'}</h3>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal review-item">
                <h3 className="main-head">To {!isFinalStep && 'account details'}</h3>
              </SummaryGridItem>
            </SummarySection>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <SummarySection columns="1fr 1fr" className="summary-section-internal">
              <SummaryGridItem className="section-item-internal review-item review-item-old">
                <label title="Also known as invoice group" className="review-item-label">
                  Billing account
                </label>
                <BillingAccount billingAccount={previousTransactionAccount} />
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal review-item">
                <label title="Also known as invoice group" className="review-item-label">
                  Billing account
                </label>
                {newTransactionAccount && <BillingAccount billingAccount={newTransactionAccount} />}
              </SummaryGridItem>
            </SummarySection>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <SummarySection columns="1fr 1fr" className="summary-section-internal">
              <SummaryGridItem className="section-item-internal review-item review-item-old">
                <label>Billing address</label>
                <div>
                  {previousTransactionAccount?.billToParty
                    ? formatAddressLabel(previousTransactionAccount.billToParty)
                    : 'No address provided'}
                </div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal review-item">
                <label>Billing address</label>
                <div>
                  {newTransactionAccount?.billToParty
                    ? formatAddressLabel(newTransactionAccount.billToParty)
                    : 'No address provided'}
                </div>
              </SummaryGridItem>
            </SummarySection>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <SummarySection columns="1fr 1fr" className="summary-section-internal">
              <SummaryGridItem className="section-item-internal review-item review-item-old">
                <label>Billing admins</label>
                <div>
                  {selectedCurrentBillingAdmin}
                  {previousTxaBillingAdminsCount > 1 && (
                    <div>
                      <a onClick={() => setPreviousShowMore(!previousShowMore)}>
                        {!previousShowMore && `+ ${previousTxaBillingAdminsCount - 1} more`}
                      </a>
                    </div>
                  )}
                  {previousShowMore &&
                    loadMore(previousTransactionAccount?.billingAdmins?.nodes, selectedCurrentBillingAdmin)}
                </div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal review-item">
                <label>Billing admins</label>
                <div>
                  {selectedNewBillingAdmin}
                  {newTxaBillingAdminsCount > 1 && (
                    <div>
                      <a onClick={() => setNewShowMore(!newShowMore)}>
                        {!newShowMore && `+ ${newTxaBillingAdminsCount - 1} more`}
                      </a>
                    </div>
                  )}
                  {newShowMore && loadMore(newTransactionAccount?.billingAdmins?.nodes, selectedNewBillingAdmin)}
                </div>
              </SummaryGridItem>
            </SummarySection>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <SummarySection columns="1fr 1fr" className="summary-section-internal">
              <SummaryGridItem className="section-item-internal review-item review-item-old review-item-billing-profile">
                <label className="review-item-label">Billing profile</label>
                <div>{getInvoiceGroupName(previousInvoiceGroup?.name)}</div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal review-item review-item-billing-profile">
                <label className="review-item-label">Billing profile</label>
                <div>{getInvoiceGroupName(newInvoiceGroup?.name)}</div>
              </SummaryGridItem>
            </SummarySection>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <SummarySection columns="1fr 1fr" className="summary-section-internal">
              <SummaryGridItem className="section-item-internal review-item review-item-old">
                <label>Sold-to address</label>
                <div>
                  {previousInvoiceGroup?.shipToParty?.id
                    ? formatAddressLabel(previousInvoiceGroup.shipToParty)
                    : 'No address provided'}
                </div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal review-item">
                <label>Sold-to address</label>
                <div>
                  {newInvoiceGroup?.shipToParty?.id
                    ? formatAddressLabel(newInvoiceGroup.shipToParty)
                    : 'No address provided'}
                </div>
              </SummaryGridItem>
            </SummarySection>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <SummarySection columns="1fr 1fr" className="summary-section-internal">
              <SummaryGridItem className="section-item-internal review-item review-item-old">
                <label>Tax ID</label>
                <div>{defaultTo(previousInvoiceGroup?.shipToParty?.taxId, 'Not provided')}</div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal review-item">
                <label>Tax ID</label>
                <div>{defaultTo(newInvoiceGroup?.shipToParty?.taxId, 'Not provided')}</div>
              </SummaryGridItem>
            </SummarySection>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <SummarySection columns="1fr 1fr" className="summary-section-internal">
              <SummaryGridItem className="section-item-internal review-item review-item-old">
                <label>Payment method</label>
                <div>
                  {previousInvoiceGroup?.defaultPaymentMethod
                    ? formatPaymentMethod(previousInvoiceGroup.defaultPaymentMethod)
                    : 'Not available'}
                </div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal review-item">
                <label>Payment method</label>
                <div>
                  {newInvoiceGroup?.defaultPaymentMethod
                    ? formatPaymentMethod(newInvoiceGroup.defaultPaymentMethod)
                    : 'Not available'}
                </div>
              </SummaryGridItem>
            </SummarySection>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <SummarySection columns="1fr 1fr" className="summary-section-internal">
              <SummaryGridItem className="section-item-internal review-item review-item-old">
                <label>Pricing</label>
                <div>{defaultTo(previousInvoiceGroup?.shipToPriceEligibility, 'Not available')}</div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal review-item">
                <label>Pricing</label>
                <div>{defaultTo(newInvoiceGroup?.shipToPriceEligibility, 'Not available')}</div>
              </SummaryGridItem>
            </SummarySection>
          </SummaryGridItem>
          <SummaryGridItem className="section-item-internal">
            <SummarySection columns="1fr 1fr" className="summary-section-internal">
              <SummaryGridItem className="section-item-internal review-item review-item-old">
                <label>Currency</label>
                <div>{defaultTo(previousInvoiceGroup?.currency, 'Not available')}</div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal review-item">
                <label>Currency</label>
                <div>{defaultTo(newInvoiceGroup?.currency, 'Not available')}</div>
              </SummaryGridItem>
            </SummarySection>
          </SummaryGridItem>
          {newPartnerDiscount && (
            <SummaryGridItem className="section-item-internal">
              <SummarySection columns="1fr 1fr" className="summary-section-internal">
                <SummaryGridItem className="section-item-internal review-item review-item-old">
                  {() => null}
                </SummaryGridItem>
                <SummaryGridItem className="section-item-internal review-item">
                  <label>Partner discount</label>
                  <div>{getPromotionTitle(newPartnerDiscount, { handleEmptyTitle: true })}</div>
                </SummaryGridItem>
              </SummarySection>
            </SummaryGridItem>
          )}
        </SummarySection>
      </div>
    </div>
  );
};
